<script>import * as d3 from "d3";
import { okhsl } from "../../../../d3-mapp/color/index.js";
import Bar from "./Bar.svelte";
export let tag = "";
export let orientation = "vertical";
export let width;
export let height;
export let padding = 5;
export let defaultFill = okhsl("transparent");
export let data;
export let axisUnit;
export let label = (datum) => datum.label;
export let value = (d) => d.value;
export let centerLabel = (data2) => Object.values(data2.distribution).reduce((sum, d) => sum + d.value, 0).toString();
export let barSize = 20;
export let showAxis = true;
export let duration = 400;
const tickPadding = 9;
const barPadding = 0.1;
const scalarAxisSize = showAxis ? 40 : 0;
const vPadding = padding;
const hPadding = padding;
const fontSize = 10;
const xAxisTitle = orientation === "vertical" ? -scalarAxisSize : width / 2;
const yAxisTitle = orientation === "vertical" ? height / 2 : tickPadding + 2 * fontSize;
const transformAxisTitle = orientation === "vertical" ? `rotate(-90, ${xAxisTitle}, ${yAxisTitle})` : "";
let barCount = Object.keys(data.distribution).length;
let maxCategoryRangeSize = (orientation === "horizontal" ? height - 2 * vPadding : width - 2 * hPadding) - scalarAxisSize;
let categoryRangeSize = Math.min(barSize * barCount, maxCategoryRangeSize);
let totalBandFreeSpace = maxCategoryRangeSize - categoryRangeSize;
let categoryAxisContainerNode;
let categoryScale;
$: {
  if (data && categoryAxisContainerNode) {
    const categoryDomain = Object.entries(data.distribution).map((entry) => label(entry[1])).reverse();
    const categoryRange = orientation === "vertical" ? [hPadding + scalarAxisSize, hPadding + scalarAxisSize + categoryRangeSize] : [vPadding + totalBandFreeSpace + categoryRangeSize, vPadding + totalBandFreeSpace];
    categoryScale = d3.scaleBand(categoryDomain, categoryRange).padding(barPadding);
    let axisGenerator = orientation === "vertical" ? d3.axisBottom(categoryScale).tickSize(0) : d3.axisLeft(categoryScale).tickSize(0);
    d3.select(categoryAxisContainerNode).call(axisGenerator);
  }
}
let scalarAxisContainerNode;
let scalarScale;
$: {
  if (data && scalarAxisContainerNode) {
    let maxValue = data.max !== void 0 ? value(data.distribution[data.max]) : 1;
    const scalarExtent = [0, maxValue];
    const scalarRange = orientation === "vertical" ? [height - 2 * vPadding, 0] : [0, width - 2 * hPadding];
    scalarScale = d3.scaleLinear(scalarExtent, scalarRange).nice();
    const axisGenerator = orientation === "vertical" ? d3.axisLeft(scalarScale) : d3.axisBottom(scalarScale);
    if (maxValue >= 1 && maxValue < 10) {
      axisGenerator.ticks(Math.ceil(maxValue));
    }
    d3.select(scalarAxisContainerNode).call(axisGenerator);
  }
}
const fgColor = (bg) => {
  const light = "#dbe4e7";
  const dark = "#2d2f36";
  return okhsl(bg).l > 0.5 ? dark : light;
};
let barData = [];
let rectDataGenerator = void 0;
$: {
  rectDataGenerator = categoryScale ? orientation === "vertical" ? (data2) => data2.map((datum, i) => {
    return {
      data: datum,
      index: i,
      value: value(datum),
      y: vPadding + scalarScale(value(datum)),
      x: categoryScale(label(datum)),
      height: height - 2 * vPadding - scalarScale(value(datum)),
      width: categoryScale.bandwidth()
    };
  }) : (data2) => data2.map((datum, i) => {
    return {
      data: datum,
      index: i,
      value: value(datum),
      x: hPadding,
      y: categoryScale(label(datum)),
      width: scalarScale(value(datum)),
      height: categoryScale.bandwidth()
    };
  }) : void 0;
}
$: {
  barData = data && rectDataGenerator ? rectDataGenerator(Object.values(data.distribution)) : [];
}
</script>
  
  <g
	class="bar-chart {tag}"
	transform={`translate(0,0)`}
	stroke="none"
	fill={defaultFill?.formatHex()}
	fill-opacity={defaultFill?.opacity}
  >
	{#if data}
	  {#if barData.length > 0}
		<g class="bars">
		  {#each barData as barDatum (barDatum.data.key)}
			<Bar title={label} {duration} {barDatum} {fgColor} {orientation} {fontSize} />
		  {/each}
		</g>
	  {/if}
	  <g
		class={`axis band-scale ${orientation === "horizontal" ? "left" : "bottom"} ${
		  showAxis ? "" : "hidden"
		}`}
		transform="translate({orientation === 'horizontal'
		  ? hPadding
		  : 0},{orientation === 'horizontal' ? 0 : height - vPadding})"
		bind:this={categoryAxisContainerNode}
	  />
	  <g
		class={`axis scalar-scale ${orientation === "horizontal" ? "bottom" : "left"}`}
		transform="translate({orientation === 'vertical'
		  ? scalarAxisSize+hPadding
		  : hPadding},{orientation === 'vertical'
		  ? vPadding
		  : height - scalarAxisSize-vPadding})"
		bind:this={scalarAxisContainerNode}
		><text y={yAxisTitle} x={xAxisTitle} transform={transformAxisTitle} text-anchor="middle"
		  >{centerLabel ? centerLabel(data) + " " : ""}{axisUnit}</text
		></g
	  >
	{/if}
  </g>
  
  <style>
	g.axis.scalar-scale :global(text),
	g.bar-chart :global(text),
	g.bar-chart text {
	  fill-opacity: 1;
	  font:
		normal 10px/1 Roboto,
		sans-serif;
	}
  
	g.axis.band-scale :global(text) {
	  fill-opacity: 0;
	  text-anchor: start;
	}
	g.axis.scalar-scale :global(text),
	g.bar-chart g.bars :global(text) {
	  fill: currentColor;
	}
	g.axis.scalar-scale :global(text) {
	  font-weight: bold;
	}
  </style>
  